import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Spinner } from '@/components/ui/atoms/spinner';

/**
 * Generate a UUID v4 string.
 * Uses crypto.randomUUID() if available (secure contexts), otherwise falls back
 * to a manual implementation using crypto.getRandomValues() or Math.random().
 */
function generateUUID(): string {
  // Try native crypto.randomUUID() first (only available in secure contexts)
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  // Fallback using crypto.getRandomValues() if available
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // Set version (4) and variant (8, 9, A, or B) bits
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
      ''
    );
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last resort fallback using Math.random() (less secure but works everywhere)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * LinkedIn OAuth Start Page
 *
 * Purpose: This page runs on the MAIN domain (local.terrakube.com) to start the
 * LinkedIn OAuth flow. This is necessary because:
 *
 * 1. OAuth PKCE flow state is stored in cookies/localStorage on the current domain
 * 2. If OAuth starts on subdomain but callback is on main domain, flow state is lost
 * 3. By starting OAuth here on the main domain, the flow state will be available
 *    when the callback returns to the main domain
 *
 * Flow:
 * 1. User on dogs.local.terrakube.com clicks "Continue with LinkedIn"
 * 2. They're redirected here: local.terrakube.com/auth/linkedin-start?community=dogs
 * 3. We store the community slug and start OAuth from the main domain
 * 4. LinkedIn redirects back to local.terrakube.com/auth/callback
 * 5. AuthCallback detects LinkedIn OAuth and redirects to dogs.local.terrakube.com/auth/linkedin-callback
 *
 * Location: /auth/linkedin-start
 */
export default function LinkedInOAuthStart() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const startOAuth = async () => {
      // Get community slug from URL params
      const communitySlug = searchParams.get('community');

      if (!communitySlug) {
        setError('Missing community parameter. Please go back and try again.');
        return;
      }

      console.log(
        'LinkedInOAuthStart: Starting OAuth for community:',
        communitySlug
      );
      console.log(
        'LinkedInOAuthStart: Current origin:',
        window.location.origin
      );

      // Generate a unique session token for this OAuth flow
      const sessionToken = generateUUID();

      // Store the OAuth context in the database - this survives across ALL domains
      // including custom domains that communities might use
      const { error: dbError } = await supabase
        .from('pending_oauth_context')
        .insert({
          session_token: sessionToken,
          community_slug: communitySlug,
          provider: 'linkedin_oidc',
        });

      if (dbError) {
        console.error(
          'LinkedInOAuthStart: Failed to store OAuth context:',
          dbError
        );
        // Fall back to localStorage only
      } else {
        console.log(
          'LinkedInOAuthStart: Stored OAuth context in database with token:',
          sessionToken
        );
      }

      // Store session token in localStorage (this will be read by AuthCallback on the main domain)
      // NOTE: We only store the session_token, not the community_slug.
      // The community_slug is stored in the database and retrieved via session_token lookup.
      // This is intentional - DB is the source of truth, localStorage just links to it.
      localStorage.setItem('linkedin_oauth_session', sessionToken);
      console.log(
        'LinkedInOAuthStart: Stored session token in localStorage:',
        sessionToken
      );

      try {
        // Start OAuth from the main domain
        // Pass the community slug in the redirectTo URL so it survives the OAuth redirect chain
        // This is more reliable than localStorage which can be lost during redirects
        const callbackUrl = `${window.location.origin}/auth/callback?linkedin_community=${encodeURIComponent(communitySlug)}`;
        console.log(
          'LinkedInOAuthStart: Callback URL with community:',
          callbackUrl
        );

        const { data, error: oauthError } = await supabase.auth.signInWithOAuth(
          {
            provider: 'linkedin_oidc',
            options: {
              redirectTo: callbackUrl,
              // Request additional scopes to get vanityName/publicProfileUrl
              // r_basicprofile includes: name, photo, headline, and public profile URL
              scopes: 'openid profile email r_basicprofile',
              queryParams: {
                access_type: 'offline',
                prompt: 'consent',
              },
              skipBrowserRedirect: false,
            },
          }
        );

        if (oauthError) {
          throw oauthError;
        }

        console.log('LinkedInOAuthStart: OAuth initiated:', data);
        // User will be redirected to LinkedIn
      } catch (err: any) {
        console.error('LinkedInOAuthStart: Error starting OAuth:', err);
        setError(err.message || 'Failed to start LinkedIn authentication');
      }
    };

    startOAuth();
  }, [searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 mx-auto flex items-center justify-center mb-4">
            <span className="text-red-600 text-xl">✕</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Something went wrong
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.history.back()}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center space-y-4 bg-gray-50">
      <Spinner size="lg" />
      <p className="text-gray-600">Connecting to LinkedIn...</p>
      <p className="text-sm text-gray-400">You'll be redirected in a moment</p>
    </div>
  );
}
